import { isEmpty } from '@shared/common'
import { AvailabilityGroupClientDto } from 'service-api/src/types/api'

/**
 * True when the unavailable reason was set by the Hatch overlay engine.
 * The DTO type does not declare `source`, so this encapsulates the cast.
 */
export const isHatchSourced = (reason: unknown): boolean =>
  (reason as { source?: string } | null | undefined)?.source === 'hatch'

export const getGroupMinimumPrice = (
  availabilityGroups: AvailabilityGroupClientDto[][] | undefined
): AvailabilityGroupClientDto => {
  if (!availabilityGroups || availabilityGroups.length == 0 || availabilityGroups[0].length == 0) {
    return {}
  }

  return availabilityGroups.reduce(
    (acc, group) => {
      const offerGroup = group[0].availabilities![0]!
      const accGroup = acc.availabilities![0]!.price!.value

      // We do not want to show price if unavailableReason is MBV
      if (offerGroup.unavailableReason?.type === 'PRICE' || isEmpty(offerGroup.price?.value)) {
        return acc
      }
      return accGroup === undefined || offerGroup.price!.value! < accGroup ? group[0] : acc
    },
    { availabilities: [{ price: { value: undefined } }] } as AvailabilityGroupClientDto
  )
}

export const getBaseGroup = (
  serviceTypeName: string,
  petGroup: number,
  availabilityGroups: AvailabilityGroupClientDto[] | AvailabilityGroupClientDto[][] | undefined
): AvailabilityGroupClientDto => {
  if (!availabilityGroups) {
    return {}
  }

  if (serviceTypeName === 'daycare') {
    const tempTestRequiredOffers = (availabilityGroups as AvailabilityGroupClientDto[][]).filter(
      (ag) =>
        ag?.[0]?.availabilities?.[0].unavailableReason?.type === 'TEMPER_TEST' ||
        ag?.[0]?.availabilities?.[0].condition?.reason?.type === 'TEMPER_TEST'
    )
    const applicableOffers = tempTestRequiredOffers.length > 0 ? tempTestRequiredOffers : availabilityGroups
    return getGroupMinimumPrice(applicableOffers as AvailabilityGroupClientDto[][])
  }

  return (availabilityGroups as AvailabilityGroupClientDto[])?.[petGroup] || {}
}

export const isDaycareOfferUnavailable = (availabilityGroups: AvailabilityGroupClientDto[][] | undefined) => {
  let allDaysUnavailable = true,
    atLeastOneUnavailableReasonApplies = false
  availabilityGroups?.forEach((group) => {
    const availability = group?.[0]?.availabilities?.[0]
    if (availability?.available) {
      allDaysUnavailable = false
    }
    if (
      isHatchSourced(availability?.unavailableReason) ||
      ['WEIGHT', 'TOTAL_WEIGHT', 'PET_COUNT', 'SALES_CHANNEL', 'OCCUPANCY'].includes(
        availability?.unavailableReason?.type || ''
      ) ||
      (availability?.unavailableReason?.type === 'TEMPER_TEST' &&
        availability.condition?.reason?.type !== 'TEMPER_TEST')
    ) {
      atLeastOneUnavailableReasonApplies = true
    }
  })
  return allDaysUnavailable && atLeastOneUnavailableReasonApplies
}

export const isDaycareOfferAvailableWithTempTest = (availabilityGroups: AvailabilityGroupClientDto[][] | undefined) => {
  return availabilityGroups?.some(
    (ag) =>
      (ag?.[0]?.availabilities?.[0].unavailableReason?.type === 'TEMPER_TEST' &&
        ag?.[0]?.availabilities?.[0].unavailableReason?.contingent) ||
      ag?.[0]?.availabilities?.[0].condition?.reason?.type === 'TEMPER_TEST'
  )
}

export const isDaycareOfferUnavailableWithTempTestFail = (
  availabilityGroups: AvailabilityGroupClientDto[][] | undefined
) => {
  return availabilityGroups?.some(
    (ag) =>
      ag?.[0]?.availabilities?.[0].unavailableReason?.type === 'TEMPER_TEST' &&
      ag?.[0]?.availabilities?.[0].unavailableReason?.temperTestFailed
  )
}

export const getRoomPrice = (
  availabilityGroup: AvailabilityGroupClientDto,
  isDaycare: boolean,
  totalPets: number | undefined
) => {
  const room = availabilityGroup.availabilities?.[0]
  if (!room || room.unavailableReason?.type === 'PRICE') {
    // We do not want to show price if unavailableReason is MBV
    return undefined
  }

  if (isDaycare) {
    return isEmpty(room.price?.value) ? undefined : room.price!.value! / (totalPets ?? 1)
  }

  return room.price?.rate
}

export const getTemperTestBadgeText = (
  availabilityGroups: AvailabilityGroupClientDto[] | AvailabilityGroupClientDto[][],
  isDaycare: boolean
): string | undefined | null => {
  const thisGroup: AvailabilityGroupClientDto = isDaycare
    ? (availabilityGroups.flat().filter((g) => g?.availabilities?.[0].temperTestMsg)[0] as AvailabilityGroupClientDto)
    : (availabilityGroups[0] as AvailabilityGroupClientDto)

  return thisGroup?.availabilities?.[0].temperTestMsg
}
